import { Fragment } from 'react';

import {
  CONDITION_TYPE_TO_LEFT_COL_TEXT,
  comparatorOptions,
  ComparatorOptionsWithNoValueRequirement,
  PURCHASE_CONDITION_QUERY_KEY,
  CONDITIONAL_TEXT,
} from 'modules/predictiveGoals/components/queryBuilder/constants';
import { HAVE_USER_FIELD, EVENT_OR_PROPERTY_LEADING_TEXT } from 'modules/predictiveGoals/constants';
import { ensureString } from '../utils';
import {
  Body2Bold,
  ConditionSpacing,
  GoalCriteriaConditionIcon,
  GoalCriteriaInlineCode,
  GoalCriteriaInlineText,
  GoalCriteriaNestedCondition,
  GoalCriteriaNextLineGroup,
  GoalCriteriaVerticalSpace,
  GoalCriteriaTopClauseContainer,
} from './Styled';
import { KeyedConditionClause } from '../types';

export type BaseComponentProps = {
  isJoinedOrClause?: boolean;
  name?: string;
  orIndex: number;
};

export type PropertyProps = BaseComponentProps & {
  label: string;
  userValue: string;
  comparator: string;
  symbol: string;
};

export type EventProps = BaseComponentProps & {
  conditions: Array<KeyedConditionClause>;
};

export const AndJoinerComponent = () => {
  return (
    <>
      <GoalCriteriaVerticalSpace />
      <Body2Bold>And also...</Body2Bold>
      <GoalCriteriaVerticalSpace />
    </>
  );
};

export const PropertyComponent = (props: PropertyProps) => {
  const { isJoinedOrClause, label, name, orIndex, userValue, comparator, symbol } = props;
  const key = `${name}${orIndex}`;
  const isUnaryComparator = ComparatorOptionsWithNoValueRequirement.includes(comparator);

  if (isJoinedOrClause) {
    return (
      <GoalCriteriaNextLineGroup key={key}>
        <GoalCriteriaInlineText>{`${CONDITIONAL_TEXT.or} ${HAVE_USER_FIELD} `}</GoalCriteriaInlineText>
        <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
        <GoalCriteriaInlineCode>{symbol || label}</GoalCriteriaInlineCode>
        {!isUnaryComparator && <GoalCriteriaInlineCode>{userValue}</GoalCriteriaInlineCode>}
      </GoalCriteriaNextLineGroup>
    );
  }

  return (
    <GoalCriteriaTopClauseContainer key={key}>
      <GoalCriteriaInlineText>{`${HAVE_USER_FIELD} `}</GoalCriteriaInlineText>
      <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
      <GoalCriteriaInlineCode>{label}</GoalCriteriaInlineCode>
      {!isUnaryComparator && <GoalCriteriaInlineCode>{userValue}</GoalCriteriaInlineCode>}
    </GoalCriteriaTopClauseContainer>
  );
};

export const UpdatedPropertyComponent = (props: PropertyProps) => {
  const { isJoinedOrClause, label, name, orIndex, userValue, comparator, symbol } = props;
  const key = `${name}${orIndex}`;
  const isUnaryComparator = ComparatorOptionsWithNoValueRequirement.includes(comparator);

  if (isJoinedOrClause) {
    return (
      <GoalCriteriaNextLineGroup key={key}>
        <GoalCriteriaInlineText>{`${CONDITIONAL_TEXT.or} ${EVENT_OR_PROPERTY_LEADING_TEXT.property} `}</GoalCriteriaInlineText>
        <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
        <GoalCriteriaInlineCode>{symbol || label}</GoalCriteriaInlineCode>
        {!isUnaryComparator && <GoalCriteriaInlineCode>{userValue}</GoalCriteriaInlineCode>}
      </GoalCriteriaNextLineGroup>
    );
  }

  return (
    <GoalCriteriaTopClauseContainer key={key}>
      <GoalCriteriaInlineText>{`${EVENT_OR_PROPERTY_LEADING_TEXT.property} `}</GoalCriteriaInlineText>
      <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
      <GoalCriteriaInlineCode>{label}</GoalCriteriaInlineCode>
      {!isUnaryComparator && <GoalCriteriaInlineCode>{userValue}</GoalCriteriaInlineCode>}
    </GoalCriteriaTopClauseContainer>
  );
};

export const ClauseJoinedContainer = (props: any) => <div>{props.children}</div>;
export const ConditionNextLineContainer = (props: any) => (
  <GoalCriteriaNestedCondition>{props.children}</GoalCriteriaNestedCondition>
);
export const InlineContainer = (props: any) => <>{props.children}</>;

export const EventComponent = (props: EventProps) => {
  const { conditions, isJoinedOrClause, name, orIndex } = props;

  const ClauseContainer = isJoinedOrClause ? ClauseJoinedContainer : InlineContainer;

  const ConditionContainer = isJoinedOrClause ? ConditionNextLineContainer : InlineContainer;

  const leadingJoinedSystemText = isJoinedOrClause && (
    <ClauseContainer>
      <GoalCriteriaInlineText>{`${CONDITIONAL_TEXT.or} ${EVENT_OR_PROPERTY_LEADING_TEXT.event} `}</GoalCriteriaInlineText>
      <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
    </ClauseContainer>
  );

  const parsedConditions = conditions.map(
    ({ key, ...clause }: KeyedConditionClause, index: number) => {
      let comparatorContext = comparatorOptions.occurrences.find(
        (option) => option.id === clause.comparator,
      );

      if (key === PURCHASE_CONDITION_QUERY_KEY) {
        comparatorContext = comparatorOptions.long.find(
          (option) => option.id === clause.comparator,
        );
      }

      const conditionName = CONDITION_TYPE_TO_LEFT_COL_TEXT[key];
      const isUnaryComparator = ComparatorOptionsWithNoValueRequirement.includes(clause.comparator);

      const criteriaComparatorAndValue = isUnaryComparator ? (
        <GoalCriteriaInlineCode>{ensureString(comparatorContext?.label)}</GoalCriteriaInlineCode>
      ) : (
        <Fragment>
          <GoalCriteriaInlineCode>{ensureString(comparatorContext?.Suffix)}</GoalCriteriaInlineCode>
          <GoalCriteriaInlineCode>{ensureString(clause?.value)}</GoalCriteriaInlineCode>
        </Fragment>
      );

      return (
        <Fragment key={`${index}${key}`}>
          {leadingJoinedSystemText}
          <ConditionContainer>
            <ConditionSpacing>
              <GoalCriteriaConditionIcon data-is-nested-join={isJoinedOrClause} />
              <GoalCriteriaInlineText>{ensureString(conditionName)}</GoalCriteriaInlineText>
              {criteriaComparatorAndValue}
            </ConditionSpacing>
          </ConditionContainer>
        </Fragment>
      );
    },
  );

  const leadingTopSystemText = !isJoinedOrClause && (
    <GoalCriteriaTopClauseContainer key={`${name}${orIndex}`} data-is-event="true">
      <GoalCriteriaInlineText>{`${EVENT_OR_PROPERTY_LEADING_TEXT.event} `}</GoalCriteriaInlineText>
      <GoalCriteriaInlineCode>{name}</GoalCriteriaInlineCode>
    </GoalCriteriaTopClauseContainer>
  );
  return (
    <>
      {leadingTopSystemText}
      <GoalCriteriaNextLineGroup data-condition={'event'} key={`${name}${orIndex + 1}`}>
        {parsedConditions}
      </GoalCriteriaNextLineGroup>
    </>
  );
};
